import React, { useEffect, useState } from "react";
import { api, formatCHF } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  kennzeichen: "",
  fahrzeugtyp: "Leasing",
  monatliche_fixkosten: 0,
  diesel_kosten: 0,
  leasing_kosten: 0,
  service_reifen: 0,
  versicherung_monatlich: 0,
  versicherer: "",
  jahrespraemie: null,
  strassenverkehr: null,
  gueltig_von: "",
  gueltig_bis: "",
  standard_fahrer_id: "",
  km_kosten: 0,
  diesel_enthalten: false,
  ist_7days_fzg: false,
  eigentum: "firma",
  eigner_fahrer_id: "",
  standard_monatspauschale: 0,
  aktiv: true,
  bemerkungen: "",
};

export default function Fahrzeuge() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [v, e] = await Promise.all([api.get("/vehicles"), api.get("/employees")]);
    setList(v.data);
    setEmployees(e.data);
  };
  useEffect(() => {
    load();
  }, []);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...EMPTY, ...v, standard_fahrer_id: v.standard_fahrer_id || "", gueltig_von: v.gueltig_von || "", gueltig_bis: v.gueltig_bis || "" });
    setOpen(true);
  };

  const sumComponents = (Number(form.diesel_kosten) || 0) + (Number(form.leasing_kosten) || 0) + (Number(form.service_reifen) || 0) + (Number(form.versicherung_monatlich) || 0);

  const save = async () => {
    try {
      const payload = {
        ...form,
        monatliche_fixkosten: Number(form.monatliche_fixkosten) || 0,
        diesel_kosten: Number(form.diesel_kosten) || 0,
        leasing_kosten: Number(form.leasing_kosten) || 0,
        service_reifen: Number(form.service_reifen) || 0,
        versicherung_monatlich: Number(form.versicherung_monatlich) || 0,
        km_kosten: Number(form.km_kosten) || 0,
        jahrespraemie: form.jahrespraemie === "" || form.jahrespraemie === null ? null : Number(form.jahrespraemie),
        strassenverkehr: form.strassenverkehr === "" || form.strassenverkehr === null ? null : Number(form.strassenverkehr),
        gueltig_von: form.gueltig_von || null,
        gueltig_bis: form.gueltig_bis || null,
        standard_fahrer_id: form.standard_fahrer_id || null,
      };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Gespeichert");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <PageContainer>
      <PageHeader title="Fahrzeuge" subtitle="Stammdaten Fahrzeugflotte" testId="fahrzeuge-page">
        <Button onClick={openNew} data-testid="add-vehicle-button" className="bg-[#0055FF] hover:bg-[#0040CC]">
          <Plus className="h-4 w-4 mr-2" /> Neues Fahrzeug
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="vehicles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Kennzeichen</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Eigentum</TableHead>
                <TableHead>Fahrer</TableHead>
                <TableHead className="text-right">Diesel</TableHead>
                <TableHead className="text-right">Leasing/Miete</TableHead>
                <TableHead className="text-right">Service</TableHead>
                <TableHead className="text-right">Vers./Monat</TableHead>
                <TableHead className="text-right">Total/Monat</TableHead>
                <TableHead className="text-right">Strassenverkehr</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-neutral-500 py-8">
                    Noch keine Fahrzeuge erfasst.
                  </TableCell>
                </TableRow>
              )}
              {list.map((v) => (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="font-mono font-semibold">{v.kennzeichen}</TableCell>
                  <TableCell>{v.fahrzeugtyp}</TableCell>
                  <TableCell>
                    {v.eigentum === "fahrer_privat" ? (
                      <div className="text-xs">
                        <Badge className="bg-blue-100 text-blue-800">Fahrer privat</Badge>
                        {v.standard_monatspauschale > 0 && (
                          <div className="text-neutral-500 mt-0.5">{formatCHF(v.standard_monatspauschale)}/Mt.</div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline">Firma</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{empName(v.eigentum === "fahrer_privat" ? v.eigner_fahrer_id : v.standard_fahrer_id)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(v.diesel_kosten)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(v.leasing_kosten)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(v.service_reifen)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(v.versicherung_monatlich)}</TableCell>
                  <TableCell className="nivara-num font-semibold">{formatCHF(v.monatliche_fixkosten)}</TableCell>
                  <TableCell className="nivara-num">{v.strassenverkehr != null ? formatCHF(v.strassenverkehr) : "—"}</TableCell>
                  <TableCell>
                    <Badge className={v.aktiv ? "bg-[#008A00] text-white" : "bg-neutral-200 text-neutral-700"}>
                      {v.aktiv ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(v.id)} data-testid={`delete-vehicle-${v.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</DialogTitle>
            <DialogDescription>
              Stammdaten, monatliche Fixkosten, Standard-Fahrer und Abrechnungs-Optionen (Diesel, 7Days-Fahrzeug) für das Fahrzeug.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Kennzeichen / Bezeichnung</Label>
              <Input value={form.kennzeichen} onChange={(e) => setForm({ ...form, kennzeichen: e.target.value })} data-testid="vehicle-kennzeichen-input" className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Fahrzeugtyp</Label>
              <Select value={form.fahrzeugtyp} onValueChange={(v) => setForm({ ...form, fahrzeugtyp: v })}>
                <SelectTrigger data-testid="vehicle-typ-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Leasing">Leasing</SelectItem>
                  <SelectItem value="Miete">Miete</SelectItem>
                  <SelectItem value="Eigentum">Eigentum</SelectItem>
                  <SelectItem value="Privatfahrzeug">Privatfahrzeug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-3">
              <Label>Standard-Fahrer</Label>
              <Select value={form.standard_fahrer_id || "_none"} onValueChange={(v) => setForm({ ...form, standard_fahrer_id: v === "_none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="— Kein Standard-Fahrer —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Kein Standard-Fahrer —</SelectItem>
                  {employees.filter((e) => e.aktiv).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-3 mt-2 mb-1 text-xs uppercase tracking-[0.1em] text-neutral-500 font-medium">Monatliche Kosten</div>

            <div className="space-y-2"><Label>Diesel (CHF)</Label><Input type="number" step="0.01" value={form.diesel_kosten} onChange={(e) => setForm({ ...form, diesel_kosten: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Leasing / Miete (CHF)</Label><Input type="number" step="0.01" value={form.leasing_kosten} onChange={(e) => setForm({ ...form, leasing_kosten: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Service / Reifen (CHF)</Label><Input type="number" step="0.01" value={form.service_reifen} onChange={(e) => setForm({ ...form, service_reifen: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Versicherung / Monat (CHF)</Label><Input type="number" step="0.01" value={form.versicherung_monatlich} onChange={(e) => setForm({ ...form, versicherung_monatlich: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2">
              <Label>Total / Monat (CHF)</Label>
              <Input type="number" step="0.01" value={form.monatliche_fixkosten || sumComponents.toFixed(2)} onChange={(e) => setForm({ ...form, monatliche_fixkosten: e.target.value })} className="font-mono font-semibold" />
              <div className="text-[11px] text-neutral-500">Auto: <span className="font-mono">CHF {sumComponents.toFixed(2)}</span> (überschreibbar)</div>
            </div>
            <div className="space-y-2"><Label>KM-Kosten (CHF/km)</Label><Input type="number" step="0.01" value={form.km_kosten} onChange={(e) => setForm({ ...form, km_kosten: e.target.value })} className="font-mono" /></div>

            <div className="sm:col-span-3 mt-2 mb-1 text-xs uppercase tracking-[0.1em] text-neutral-500 font-medium">Versicherung & Strassenverkehr (jährlich)</div>

            <div className="space-y-2"><Label>Versicherer</Label><Input value={form.versicherer || ""} onChange={(e) => setForm({ ...form, versicherer: e.target.value })} placeholder="z.B. Smile" /></div>
            <div className="space-y-2"><Label>Jahresprämie (CHF)</Label><Input type="number" step="0.01" value={form.jahrespraemie ?? ""} onChange={(e) => setForm({ ...form, jahrespraemie: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Strassenverkehr (CHF/Jahr)</Label><Input type="number" step="0.01" value={form.strassenverkehr ?? ""} onChange={(e) => setForm({ ...form, strassenverkehr: e.target.value })} className="font-mono" /></div>

            <div className="space-y-2"><Label>Gültig von</Label><Input type="date" value={form.gueltig_von || ""} onChange={(e) => setForm({ ...form, gueltig_von: e.target.value })} className="font-mono" /></div>
            <div className="space-y-2"><Label>Gültig bis</Label><Input type="date" value={form.gueltig_bis || ""} onChange={(e) => setForm({ ...form, gueltig_bis: e.target.value })} className="font-mono" /></div>

            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <Label>Diesel enthalten</Label>
              <Switch checked={form.diesel_enthalten} onCheckedChange={(v) => setForm({ ...form, diesel_enthalten: v })} />
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3">
              <div>
                <Label className="block">7Days-Fahrzeug</Label>
                <div className="text-xs text-neutral-500 mt-0.5">KM werden in Kundenabrechnung abgezogen</div>
              </div>
              <Switch checked={form.ist_7days_fzg} onCheckedChange={(v) => setForm({ ...form, ist_7days_fzg: v })} data-testid="vehicle-7days-switch" />
            </div>
            <div className="sm:col-span-2 border border-neutral-200 rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm uppercase tracking-wider text-neutral-700">Eigentum</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={form.eigentum === "firma" ? "default" : "outline"} onClick={() => setForm({ ...form, eigentum: "firma", eigner_fahrer_id: "", standard_monatspauschale: 0 })} className={form.eigentum === "firma" ? "bg-[#0055FF] hover:bg-[#0040CC]" : ""} data-testid="vehicle-eigentum-firma">Firma</Button>
                  <Button type="button" size="sm" variant={form.eigentum === "fahrer_privat" ? "default" : "outline"} onClick={() => setForm({ ...form, eigentum: "fahrer_privat" })} className={form.eigentum === "fahrer_privat" ? "bg-[#0055FF] hover:bg-[#0040CC]" : ""} data-testid="vehicle-eigentum-privat">Fahrer privat</Button>
                </div>
              </div>
              {form.eigentum === "fahrer_privat" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Eigentümer (Fahrer)</Label>
                    <Select value={form.eigner_fahrer_id || "_none"} onValueChange={(v) => setForm({ ...form, eigner_fahrer_id: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="vehicle-eigner-select"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Wählen —</SelectItem>
                        {employees.filter((e) => e.aktiv).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Standard-Monatspauschale CHF</Label>
                    <Input type="number" step="0.01" value={form.standard_monatspauschale} onChange={(e) => setForm({ ...form, standard_monatspauschale: e.target.value })} className="font-mono" data-testid="vehicle-pauschale" />
                    <div className="text-xs text-neutral-500">Vorschlag in Fahrerlohn-Dialog (kann pro Monat angepasst werden).</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border border-neutral-200 rounded-md p-3 sm:col-span-2">
              <Label>Aktiv</Label>
              <Switch checked={form.aktiv} onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
            </div>

            <div className="space-y-2 sm:col-span-3">
              <Label>Bemerkungen</Label>
              <Textarea value={form.bemerkungen || ""} onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} data-testid="vehicle-save" className="bg-[#0055FF] hover:bg-[#0040CC]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
